import { Copy, FileText, Info, UploadCloud, X } from "lucide-react";
import { useRef, useState } from "react";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";

const BANK_DETAILS = [
  { label: "Account Name", value: "Digi India Solutions" },
  { label: "Account Number", value: "50100XXXXXX8842" },
  { label: "IFSC", value: "HDFC0001234" },
  { label: "UPI ID", value: "digiindia@hdfcbank" },
];

const QUICK_AMOUNTS = [1000, 2500, 5000, 10000];

const PAYMENT_MODES = [
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "Cash Deposit", label: "Cash Deposit" },
  { value: "Cheque", label: "Cheque" },
];

interface RequestTopupModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    amount: number;
    paymentMode: string;
    referenceNumber: string;
    paymentDate: string;
    remarks: string;
    receiptFileName: string;
  }) => Promise<void>;
}

export default function RequestTopupModal({ open, onClose, onSubmit }: RequestTopupModalProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [reference, setReference] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");

  function pickFile(f: File | undefined) {
    if (!f) return;
    setFile(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    pickFile(e.dataTransfer.files?.[0]);
  }

  function copyValue(value: string) {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Could not copy to clipboard"));
  }

  async function handleSubmit() {
    if (!amount || Number(amount) < 100) {
      setError("The minimum top-up amount is ₹100");
      return;
    }
    if (!paymentMode) {
      setError("Please select a payment mode");
      return;
    }
    if (paymentMode !== "Cash Deposit" && !reference.trim()) {
      setError("Please enter the payment reference number");
      return;
    }
    if (!paymentDate) {
      setError("Please select the payment date");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await onSubmit({
        amount: Number(amount),
        paymentMode,
        referenceNumber: reference.trim(),
        paymentDate,
        remarks: remarks.trim(),
        receiptFileName: file?.name ?? "",
      });
      setAmount("");
      setPaymentMode("");
      setReference("");
      setPaymentDate("");
      setRemarks("");
      setFile(null);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Could not submit your top-up request");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request wallet top-up"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            Submit Request
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-secondary">
          Transfer the amount first, then submit the payment reference for admin approval.
        </p>

        <div className="flex items-start gap-3 rounded-[10px] border border-info-bg bg-info-bg p-4">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-info">
            <Info className="h-5 w-5" />
          </span>
          <div className="flex w-full flex-col gap-2.5">
            {BANK_DETAILS.map((b) => (
              <div key={b.label} className="flex items-center justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-[13px] text-ink-secondary">{b.label}</span>
                  <span className="text-sm font-medium text-ink">{b.value}</span>
                </div>
                <button
                  type="button"
                  onClick={() => copyValue(b.value)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-white hover:text-primary-500 cursor-pointer"
                  aria-label={`Copy ${b.label}`}
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-ink">
            Amount <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-0 top-0 flex h-[42px] w-12 items-center justify-center rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-sm font-medium text-ink">
              ₹
            </span>
            <input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="h-[42px] w-full rounded-lg border border-slate-300 bg-white pl-14 pr-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500/30"
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {QUICK_AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAmount(String(a))}
                className={`inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-medium transition-colors cursor-pointer ${
                  amount === String(a)
                    ? "border-primary-500 bg-primary-50 text-primary-600"
                    : "border-slate-300 text-ink-secondary hover:border-primary-300 hover:text-primary-500"
                }`}
              >
                ₹{a.toLocaleString("en-IN")}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Payment Mode"
            required
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
            options={PAYMENT_MODES}
            placeholder="Select payment mode"
          />
          <DatePicker
            label="Payment Date"
            required
            value={paymentDate}
            onChange={setPaymentDate}
            placeholder="Select payment date"
          />
        </div>

        <Input
          label="Reference Number"
          required
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Enter reference number"
          helper="UTR number, UPI reference or cheque number"
        />

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-ink">Upload Receipt</label>
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 px-4 py-6 text-center transition-colors hover:border-primary-400"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-500">
              <UploadCloud className="h-5 w-5" />
            </span>
            <p className="mt-2 text-sm text-ink">Click to upload or drag and drop</p>
            <p className="mt-0.5 text-xs text-ink-secondary">JPG, PNG or PDF, maximum 2 MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          {file && (
            <div className="mt-2 flex items-center gap-2.5 rounded-lg border border-line bg-white px-3 py-2.5">
              <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
              <span className="flex-1 truncate text-[13px] text-ink">{file.name}</span>
              <span className="text-xs text-ink-secondary">{(file.size / 1024).toFixed(1)} KB</span>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="flex h-6 w-6 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                aria-label="Remove receipt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <Textarea
          label="Remarks"
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any additional details for the admin (optional)"
        />

        {error && <p className="text-[13px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}